import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Purchase {
  id: string;
  material_id: string;
  order_id: string;
  purchased_at: string;
  download_count: number;
  material: {
    title: string;
    category: string;
    grade_level: string;
    price: number;
    thumbnail_url: string;
    full_file_url: string;
  };
  order: {
    amount: number;
  };
}

interface Coupon {
  id: string;
  coupon_code: string;
  discount_amount: number;
  discount_type: string;
  min_purchase_amount: number;
  expires_at: string;
  is_used: boolean;
  used_at: string | null;
  source: string;
  created_at: string;
}

interface CouponData {
  available: Coupon[];
  used: Coupon[];
  expired: Coupon[];
  total: number;
}

export default function ProfilePage() {
  const { user, signOut } = useAuth();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'price' | 'downloads'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState<'purchases' | 'coupons'>('purchases');
  const [coupons, setCoupons] = useState<CouponData | null>(null);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.email) {
      fetchPurchases();
      fetchCoupons();
    }
  }, [user]);

  const fetchPurchases = async () => {
    try {
      const { data, error } = await supabase
        .from('user_purchases')
        .select(`
          *,
          material:materials(*),
          order:orders(amount)
        `)
        .eq('user_email', user?.email)
        .order('purchased_at', { ascending: false });

      if (error) throw error;
      setPurchases(data || []);
    } catch (error) {
      console.error('獲取購買記錄失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    if (!user?.email) return;
    
    setLoadingCoupons(true);
    try {
      const response = await fetch(
        `https://fnhsumfewqfgpjteloga.supabase.co/functions/v1/get-user-coupons?user_email=${encodeURIComponent(user.email)}`
      );
      const data = await response.json();
      setCoupons(data);
    } catch (error) {
      console.error('獲取優惠券失敗:', error);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const handleDownload = async (purchase: Purchase) => {
    if (!purchase.material?.full_file_url) return;

    try {
      await supabase
        .from('user_purchases')
        .update({
          download_count: purchase.download_count + 1,
          last_download_at: new Date().toISOString(),
        })
        .eq('id', purchase.id);

      window.open(purchase.material.full_file_url, '_blank');
      fetchPurchases();
    } catch (error) {
      console.error('下載失敗:', error);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/';
  };

  // 搜尋和篩選
  const filteredPurchases = purchases
    .filter(purchase => {
      const searchLower = searchQuery.toLowerCase();
      return (
        purchase.material?.title?.toLowerCase().includes(searchLower) ||
        purchase.material?.category?.toLowerCase().includes(searchLower) ||
        purchase.material?.grade_level?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      let compareValue = 0;
      
      switch (sortBy) {
        case 'date':
          compareValue = new Date(a.purchased_at).getTime() - new Date(b.purchased_at).getTime();
          break;
        case 'price':
          compareValue = (a.order?.amount || 0) - (b.order?.amount || 0);
          break;
        case 'downloads':
          compareValue = a.download_count - b.download_count;
          break;
      }
      
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 flex items-center justify-center mx-auto bg-orange-100 rounded-full">
            <i className="ri-lock-line text-4xl text-orange-500"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">請先登入</h2>
          <p className="text-gray-600">您需要登入才能查看會員中心</p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors cursor-pointer"
          >
            前往登入
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/46c4b423-33ec-4c59-b54c-70c60984f453.png" alt="Logo" className="h-10 w-10 object-contain" />
              <span className="text-xl font-bold text-gray-900">數學學習平台</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/materials" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                教材商店
              </Link>
              <button
                onClick={handleSignOut}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                登出
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-8 mb-8 shadow-sm">
            <div className="flex items-center space-x-6">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-blue-900 to-orange-500 rounded-full text-white text-3xl font-bold">
                {user.user_metadata?.full_name?.[0] || user.email?.[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {user.user_metadata?.full_name || '會員'}
                </h1>
                <p className="text-gray-600">{user.email}</p>
                <p className="text-sm text-gray-500 mt-1">
                  註冊日期：{new Date(user.created_at).toLocaleDateString('zh-TW')}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-900">{purchases.length}</div>
                  <div className="text-sm text-gray-600">已購買教材</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-500">{coupons?.available.length || 0}</div>
                  <div className="text-sm text-gray-600">可用優惠券</div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-3xl shadow-sm mb-6">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('purchases')}
                className={`flex-1 py-4 px-6 text-center font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'purchases'
                    ? 'text-blue-900 border-b-2 border-blue-900'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-shopping-bag-line mr-2"></i>
                我的購買
              </button>
              <button
                onClick={() => setActiveTab('coupons')}
                className={`flex-1 py-4 px-6 text-center font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'coupons'
                    ? 'text-blue-900 border-b-2 border-blue-900'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-coupon-line mr-2"></i>
                我的優惠券
                {coupons && coupons.available.length > 0 && (
                  <span className="ml-2 px-2 py-1 bg-orange-500 text-white rounded-full text-xs">
                    {coupons.available.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Purchases Section */}
          {activeTab === 'purchases' && (
            <div className="bg-white rounded-3xl p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">我的購買</h2>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600">共 {filteredPurchases.length} 筆記錄</span>
                </div>
              </div>

              {/* Search and Filter */}
              <div className="mb-6 space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="搜尋教材名稱、分類或年級..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-6 py-3 pr-12 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center">
                    <i className="ri-search-line text-xl text-gray-400"></i>
                  </div>
                </div>

                {/* Sort Options */}
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">排序：</span>
                  <button
                    onClick={() => {
                      if (sortBy === 'date') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortBy('date');
                        setSortOrder('desc');
                      }
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === 'date'
                        ? 'bg-blue-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    購買日期 {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <button
                    onClick={() => {
                      if (sortBy === 'price') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortBy('price');
                        setSortOrder('desc');
                      }
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === 'price'
                        ? 'bg-blue-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    金額 {sortBy === 'price' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <button
                    onClick={() => {
                      if (sortBy === 'downloads') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortBy('downloads');
                        setSortOrder('desc');
                      }
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === 'downloads'
                        ? 'bg-blue-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    下載次數 {sortBy === 'downloads' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                </div>
              ) : filteredPurchases.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                    <i className="ri-shopping-bag-line text-4xl text-gray-400"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {searchQuery ? '找不到相關教材' : '尚未購買任何教材'}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {searchQuery ? '請嘗試調整搜尋條件' : '前往教材商店選購您需要的教材'}
                  </p>
                  {!searchQuery && (
                    <Link
                      to="/materials"
                      className="inline-block px-6 py-3 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors cursor-pointer"
                    >
                      前往教材商店
                    </Link>
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredPurchases.map((purchase) => (
                    <div key={purchase.id} className="border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="h-48 bg-gray-100 relative w-full">
                        <img
                          src={purchase.material?.thumbnail_url || `https://public.readdy.ai/ai/img_res/40a3a2103e9f80f4d8b1784bcc224d9e.jpg$%7Bpurchase.material%3F.category%7D%20$%7Bpurchase.material%3F.grade_level%7D%20level%2C%20professional%20educational%20design%2C%20clean%20modern%20layout%2C%20soft%20background&width=300&height=200&seq=${purchase.material_id}&orientation=landscape`}
                          alt={purchase.material?.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-5 space-y-3">
                        <div className="flex items-center space-x-2">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium">
                            {purchase.material?.grade_level}
                          </span>
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-md text-xs font-medium">
                            {purchase.material?.category}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900 line-clamp-2">
                          {purchase.material?.title}
                        </h3>
                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <span>購買日期</span>
                          <span>{new Date(purchase.purchased_at).toLocaleDateString('zh-TW')}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-bold text-gray-900">HK$ {purchase.materials.price}</span>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDownload(purchase.materials.full_file_url, purchase.id)}
                              className="px-4 py-2 bg-green-600 text-white rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                            >
                              <i className="ri-download-line mr-1"></i>
                              下載
                            </button>
                            <button
                              onClick={() => navigate(`/materials/${purchase.material_id}`)}
                              className="px-4 py-2 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer text-sm"
                            >
                              查看詳情
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Coupons Section */}
          {activeTab === 'coupons' && (
            <div className="bg-white rounded-3xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">我的優惠券</h2>

              {loadingCoupons ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                </div>
              ) : !coupons || coupons.total === 0 ? (
                <div className="text-center py-20">
                  <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                    <i className="ri-coupon-line text-4xl text-gray-400"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">暫無優惠券</h3>
                  <p className="text-gray-600 mb-6">評價已購買的教材即可獲得優惠券</p>
                  <Link
                    to="/materials"
                    className="inline-block px-6 py-3 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors cursor-pointer"
                  >
                    前往教材商店
                  </Link>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Available Coupons */}
                  {coupons.available.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <i className="ri-checkbox-circle-line text-green-600 mr-2"></i>
                        可使用 ({coupons.available.length})
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {coupons.available.map((coupon) => (
                          <div key={coupon.id} className="relative overflow-hidden rounded-2xl border-2 border-green-500 bg-gradient-to-br from-green-50 to-white p-6">
                            <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/10 rounded-full -mr-10 -mt-10"></div>
                            <div className="relative">
                              <div className="flex items-center justify-between mb-3">
                                <span className="px-3 py-1 bg-green-600 text-white rounded-full text-xs font-semibold">
                                  {coupon.source === 'review_reward' ? '評價獎勵' : '優惠券'}
                                </span>
                                <span className="text-2xl font-bold text-green-600">
                                  {coupon.discount_type === 'percentage' ? `${coupon.discount_amount}%` : `NT$ ${coupon.discount_amount}`}
                                </span>
                              </div>
                              <div className="mb-4">
                                <div className="text-sm text-gray-600 mb-1">優惠券代碼</div>
                                <div className="font-mono font-bold text-xl text-gray-900 tracking-wider">
                                  {coupon.coupon_code}
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-600">
                                <span>
                                  {coupon.min_purchase_amount > 0 
                                    ? `滿 NT$ ${coupon.min_purchase_amount} 可用` 
                                    : '無門檻'}
                                </span>
                                <span>
                                  到期：{new Date(coupon.expires_at).toLocaleDateString('zh-TW')}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Used Coupons */}
                  {coupons.used.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <i className="ri-check-double-line text-gray-400 mr-2"></i>
                        已使用 ({coupons.used.length})
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {coupons.used.map((coupon) => (
                          <div key={coupon.id} className="relative overflow-hidden rounded-2xl border border-gray-300 bg-gray-50 p-6 opacity-60">
                            <div className="flex items-center justify-between mb-3">
                              <span className="px-3 py-1 bg-gray-400 text-white rounded-full text-xs font-semibold">
                                已使用
                              </span>
                              <span className="text-2xl font-bold text-gray-600">
                                {coupon.discount_type === 'percentage' ? `${coupon.discount_amount}%` : `NT$ ${coupon.discount_amount}`}
                              </span>
                            </div>
                            <div className="mb-4">
                              <div className="text-sm text-gray-600 mb-1">優惠券代碼</div>
                              <div className="font-mono font-bold text-xl text-gray-700 tracking-wider line-through">
                                {coupon.coupon_code}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              使用時間：{coupon.used_at ? new Date(coupon.used_at).toLocaleDateString('zh-TW') : '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expired Coupons */}
                  {coupons.expired.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <i className="ri-time-line text-gray-400 mr-2"></i>
                        已過期 ({coupons.expired.length})
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {coupons.expired.map((coupon) => (
                          <div key={coupon.id} className="relative overflow-hidden rounded-2xl border border-gray-300 bg-gray-50 p-6 opacity-50">
                            <div className="flex items-center justify-between mb-3">
                              <span className="px-3 py-1 bg-gray-400 text-white rounded-full text-xs font-semibold">
                                已過期
                              </span>
                              <span className="text-2xl font-bold text-gray-600">
                                {coupon.discount_type === 'percentage' ? `${coupon.discount_amount}%` : `NT$ ${coupon.discount_amount}`}
                              </span>
                            </div>
                            <div className="mb-4">
                              <div className="text-sm text-gray-600 mb-1">優惠券代碼</div>
                              <div className="font-mono font-bold text-xl text-gray-700 tracking-wider line-through">
                                {coupon.coupon_code}
                              </div>
                            </div>
                            <div className="text-xs text-red-500">
                              已於 {new Date(coupon.expires_at).toLocaleDateString('zh-TW')} 過期
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © 2025 數學學習平台. 版權所有
            </div>
            <div>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
